import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDrop } from 'react-dnd';
import { CODE_LABEL_HEIGHT } from 'constants/DimensionsConstants';
import { DragItemTypes } from 'constants/DragConstants';
import useMergeCode from './codes/mutations/useMergeCode';
import { useCode } from './codes/queries';
import useTrackingPage from './useTrackingPage';

const FULL_HEIGHT = CODE_LABEL_HEIGHT.toString() + "px";
const MERGE_HEIGHT = (CODE_LABEL_HEIGHT * 2).toString() + "px";

/**
 * Custom hook to manage merge interaction state and handlers
 * Handles the UI flow: hover → initiate → confirm/cancel
 * Includes drag drop logic and merge mutation handling
 */
export function useMergeInteraction({ 
  id, 
  name, // Current code's name (the merge target)
  onShowMerge, 
  initiateMergeCode, 
  confirmMerge, 
  cancelMerge,
  setConfirmDrag // Function to set confirm dragging state in parent
}) {
  const [mergeHover, setMergeHover] = useState(false);
  const [showConfirmMerge, setShowConfirmMerge] = useState(false);
  const [draggedCodeId, setDraggedCodeId] = useState(null);

  // Initialize the merge code mutation
  const { mutate: mergeCodes, isPending: isMerging } = useMergeCode();

  const page = useTrackingPage();
  const trackingContext = useMemo(() => ({ page }), [page]);

  // Fetch the dragged code's data to get its name
  const { data: draggedCode } = useCode(draggedCodeId);

  // Create the merged name: "TargetName | DraggedName"
  const mergedName = useMemo(() => {
    if (!draggedCode?.name || !name || (!mergeHover && !showConfirmMerge)) return null;
    return `${name} | ${draggedCode.name}`;
  }, [name, draggedCode?.name, mergeHover, showConfirmMerge]);

  // Drag drop logic - moved up from CodeLabelRight
  const [{ isOver }, drop] = useDrop({
    accept: DragItemTypes.CODE_LABEL,
    hover: (item, monitor) => {
      // Set dragged code ID immediately when hovering starts
      setDraggedCodeId(item.codeID);
    },
    drop: (item, monitor) => {
      // Keep the dragged code ID and show confirmation
      setDraggedCodeId(item.codeID);
      setShowConfirmMerge(true);
      // Set the confirm dragging state for ghost display during confirmation
      if (setConfirmDrag) {
        setConfirmDrag(item.codeID);
      }
      // Still call the original initiateMergeCode for UI updates
      if (initiateMergeCode) {
        initiateMergeCode(item.codeID, id);
      }
    },
    canDrop: () => true,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  // Only send hover updates if we're not in confirmation mode
  // This prevents the UI from hiding when drag ends but we're still confirming
  useEffect(() => {
    if (!showConfirmMerge) {
      onMergeHover(isOver);
      // Clear draggedCodeId when hover ends (but not during confirmation)
      if (!isOver && !showConfirmMerge) {
        setDraggedCodeId(null);
      }
    }
  }, [isOver, showConfirmMerge]);

  // Handle merge hover - called from MergeDropZone
  const onMergeHover = useCallback((isOver) => {
    const newMergeHover = !!isOver;
    setMergeHover(newMergeHover);
    onShowMerge(newMergeHover ? id : null);
  }, [mergeHover, onShowMerge, id]);

  // Handle merge initiation - called when drop occurs
  const handleInitiateMerge = useCallback((draggedCodeId, targetCodeId) => {
    setShowConfirmMerge(true);
    // Notify parent for height recalculation
    onShowMerge(id);
    // Call original handler if needed
    if (initiateMergeCode) {
      initiateMergeCode(draggedCodeId, targetCodeId);
    }
  }, [initiateMergeCode, onShowMerge, id]);

  // Handle merge cancellation
  const handleCancelMerge = useCallback(() => {
    setDraggedCodeId(null);
    setShowConfirmMerge(false);
    setMergeHover(false);
    // Clear the confirm dragging state to remove ghost effect
    if (setConfirmDrag) {
      setConfirmDrag(null);
    }
    // Notify parent for height recalculation
    onShowMerge(null);
    // Call parent's cancel merge
    if (cancelMerge) {
      cancelMerge();
    }
  }, [cancelMerge, onShowMerge, setConfirmDrag]);

  // Handle merge confirmation - now uses the actual merge mutation
  const handleConfirmMerge = useCallback(() => {
    if (draggedCodeId && id) {
      // Call the merge mutation
      mergeCodes(
        { mergeCodeId: draggedCodeId, mergeIntoCodeId: id, trackingContext },
        {
          onSuccess: () => {
            // Reset state after successful merge
            setDraggedCodeId(null);
            setShowConfirmMerge(false);
            setMergeHover(false);
            // Clear the confirm dragging state to remove ghost effect
            if (setConfirmDrag) {
              setConfirmDrag(null);
            }
            // Notify parent for height recalculation
            onShowMerge(null);
            // Call the original confirmMerge for UI updates if provided
            if (confirmMerge) confirmMerge();
          },
          onError: (error) => {
            console.error("Failed to merge codes:", error);
            // Call cancel to reset UI state
            handleCancelMerge();
          }
        }
      );
    } else if (confirmMerge) {
      // Fall back to original implementation if we don't have IDs
      confirmMerge();
    }
  }, [draggedCodeId, id, mergeCodes, confirmMerge, onShowMerge, handleCancelMerge, trackingContext]);

  // Computed values
  const isInMergeMode = mergeHover || showConfirmMerge;
  const getMergeHeight = () => isInMergeMode ? MERGE_HEIGHT : FULL_HEIGHT;

  return {
    // State
    mergeHover,
    showConfirmMerge,
    isInMergeMode,
    draggedCodeId,
    isMerging,
    
    // Handlers
    onMergeHover,
    handleInitiateMerge,
    handleConfirmMerge,
    handleCancelMerge,
    
    // Computed values
    getMergeHeight,
    mergedName,
    
    // Drop zone ref - pass this down to the actual drop zone component
    dropRef: drop
  };
}